import { Rocket, ShieldCheck, Zap, Users2 } from "lucide-react";
import { motion } from "framer-motion";

const reasons = [
  { icon: Rocket, title: "Ship in weeks, not quarters", desc: "Opinionated scaffolds, proven playbooks and senior engineers = early traction.", bg: "bg-[#FF477E]", light: true },
  { icon: Zap, title: "AI-first by default", desc: "Every product gets LLM features baked in — not bolted on as an afterthought.", bg: "bg-[#FBD46D]" },
  { icon: ShieldCheck, title: "Enterprise-grade quality", desc: "Typed APIs, tested code, secure defaults, observable deployments.", bg: "bg-[#4D96FF]", light: true },
  { icon: Users2, title: "One team, end-to-end", desc: "Design, engineering, DevOps and QA under one roof. No hand-offs, no finger-pointing.", bg: "bg-[#6BCB77]" },
];

export const WhyUs = () => {
  return (
    <section
      id="why-us"
      data-testid="why-us-section"
      className="py-24 md:py-32 bg-white border-b-4 border-gray-900 relative"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-14 items-start">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.55 }}
        >
          <span className="inline-block bg-[#FBD46D] border-[3px] border-gray-900 rounded-full px-4 py-1 text-xs font-bold uppercase tracking-[0.2em] nb-shadow-sm">
            Why Paragon
          </span>
          <h2 className="font-display mt-5 text-4xl sm:text-5xl md:text-6xl leading-tight">
            Not another<br /> dev agency.
          </h2>
          <p className="mt-6 text-lg md:text-xl font-medium text-gray-700 max-w-lg">
            We're a tight, senior crew that treats your product like our own. No
            offshore dashboards, no ticket factories — just engineers who care
            about the outcome.
          </p>

          <div className="mt-8 relative">
            <div className="bg-gray-900 border-[4px] border-gray-900 rounded-2xl overflow-hidden nb-shadow">
              <img
                data-testid="why-us-image"
                src="https://images.unsplash.com/photo-1606857521015-7f9fcf423740?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjB2aWJyYW50JTIwdGVjaCUyMG9mZmljZSUyMHN0YXJ0dXB8ZW58MHx8fHwxNzc2ODY1MDQwfDA&ixlib=rb-4.1.0&q=85"
                alt="Vibrant tech office"
                className="w-full h-72 md:h-80 object-cover"
              />
            </div>
            <div className="absolute -bottom-5 -right-5 bg-[#FF477E] text-white border-[4px] border-gray-900 rounded-xl px-4 py-3 nb-shadow-sm rotate-3">
              <div className="font-display text-3xl">98%</div>
              <div className="text-xs font-bold uppercase tracking-widest">Client retention</div>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {reasons.map((r, i) => {
            const Icon = r.icon;
            return (
              <motion.div
                key={r.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.45, delay: i * 0.08 }}
                data-testid={`reason-card-${i}`}
                className={`${r.bg} ${r.light ? "text-white" : "text-gray-900"} border-[4px] border-gray-900 rounded-2xl p-6 md:p-7 nb-shadow nb-hover`}
              >
                <div className={`w-12 h-12 rounded-lg border-[3px] border-gray-900 flex items-center justify-center mb-4 ${r.light ? "bg-white text-gray-900" : "bg-white"}`}>
                  <Icon size={22} />
                </div>
                <h3 className="font-display text-xl md:text-2xl leading-tight mb-2">{r.title}</h3>
                <p className={`text-base font-medium leading-relaxed ${r.light ? "text-white/90" : "text-gray-800"}`}>
                  {r.desc}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default WhyUs;
